// @ts-strict-ignore
/**
 * @module UsersLists
 */

import { BehaviorSubject, Subject, Subscription, filter } from "rxjs";
import { RBEvent } from "rainbow-web-sdk/models/event.model";
import { UsersList, UsersListProfile, UsersListRB } from "../../models/usersList.model";
import { Contact } from 'rainbow-web-sdk/models/contact.model';
import { User } from "rainbow-web-sdk/models/user.model";
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { Service } from "rainbow-web-sdk/services/service";
import { allSettled } from "rainbow-web-sdk/utils/allSettled";

/** @internal */
export const USERSLISTS_SVC = 'UsersListsService';

/**
 * @eventProperty
 * UsersLists service events send to other services
 * Can listen to it via `UsersListsService.subscribe()` API
 */
export enum UsersListsServiceEvents {

    /**
     * @eventProperty
     * This `RBEvent` is sent when a new users list has been created.
     *
     * **Event Data**
     * - **data**: The new created users list (as UsersList model)
     */
    ON_USERS_LIST_CREATED = "ON_USERS_LIST_CREATED",

    /**
     * @eventProperty
     * This `RBEvent` is sent when the list information (name, description or favorite status) has been modified.
     *
     * **Event Data**
     * - **data**: The updated users list (as UsersList object)
     */
    ON_USERS_LIST_PROFILE_UPDATED = "ON_USERS_LIST_PROFILE_UPDATED",

    /**
     * @eventProperty
     * This `RBEvent` is sent when a users list has been deleted.
     *
     * **Event Data**
     * - **data**: The id (as string) of the users list that has been deleted.
     */
    ON_USERS_LIST_DELETED = "ON_USERS_LIST_DELETED",

    /**
     * @eventProperty
     * This `RBEvent` is sent when a user has been added to the list.
     *
     * **Event Data**
     * - **user**: The user (as User object) added to the list
     * - **list**: The users list (as UsersList object) to which the user has been added.
     */
    ON_USER_ADDED_TO_LIST = "ON_USER_ADDED_TO_LIST",

    /**
     * @eventProperty
     * This `RBEvent` is sent when a user has been removed from the list.
     *
     * **Event Data**
     * - **userId**: The id (as string) of the user removed from the list.
     * - **list**: The users list (as UsersList object) to which the user has been added.
     */
    ON_USER_REMOVED_FROM_LIST = "ON_USER_REMOVED_FROM_LIST",
}

export interface UsersListsService {

    /**
     * Subscribe to the users lists updates, all events are of RBEvent<UsersListsServiceEvents>
     * @param callback - The callback function that will be called when events are received
     * @param eventNames - (optional) array of event to listen
     * @returns Returns RxJS Subscription
     */
    subscribe(callback: (event: RBEvent<UsersListsServiceEvents>) => any, eventNames?: UsersListsServiceEvents | UsersListsServiceEvents[]): Subscription;

    /**
     * Get all users lists with all their properties.
     */
    getUsersLists(): UsersList[];

    /**
     * Get the data associated with the specified users list.
     * @param id - The id of the users list
     */
    getUsersListById(id: string): UsersList;

    /**
     * Create a new list of users
     * Only the name of the users list is mandatory.
     * The other information (description and favorite status) are optional and can be set afterwards using the 'updateUsersListInfo' method.
     * A ON_USERS_LIST_CREATED event is sent afterwards at the completion of the operation to notify about the users list creation.
     * @param profile name - Name of the users list<br/>
     * description - (optional) A description of the users list. If not specified, set by default to "".<br/>
     * favorite - (optional) Flag indicating if the users list is marked as "favorite". If not specified, set by default to false.
     */
    createUsersList(profile: UsersListProfile): Promise<any>;

    /**
     * Delete a users list specified either by its reference or by its unique identifier.
     * A ON_USERS_LIST_DELETED event is sent afterwards at the completion of the operation to notify about the users list deletion.
     * @param listRef - Reference or identifier of the users list to delete
     */
    deleteUsersList(listRef: string | UsersList): Promise<any>;
}

/**
 * @internal
 */
export class UsersListsServiceRB extends Service implements UsersListsService {

    private portalURL: string = null;
    private xmppManagementHandler: any = null;
    private startInProgress: boolean = false;

    // Services
    private authService: AuthService = AuthService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();

    // Observables for events sent by UsersListsServiceRB
    private rxSubject: Subject<any> = new Subject<any>();

    // Observable indicating when the start of the UsersList Service is completed
    public serviceReady = new BehaviorSubject<boolean>(false);

    // List of users lists (cache)
    // Can not be directly used by UCAAS components, 'getUsersLists' method must be used.
    private usersLists: UsersList[] = [];

    /**
     * Returns the UsersListRB service singleton
     */
    public static getInstance(): UsersListsServiceRB {
        return Service.create(USERSLISTS_SVC, UsersListsServiceRB);
    }

    public static build(): UsersListsServiceRB {
        return new UsersListsServiceRB();
    }

    private constructor() {
        super();
    }

    // ------------------------------------------------------------
    //                      LIFE CYCLE STUFF
    // ------------------------------------------------------------
    public async start(): Promise<void> {
        try {
            if (!this.startInProgress) {
                this.startInProgress = true;
                this.logger.info("");
                this.logger.info(`[UsersListsService] === STARTING ===`);

                // Get and store End User portalURL
                this.portalURL = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.authService.userId}/`;

                // Don't start service for CPaaS guest
                if (this.contactService.userContact.isCPaaSGuest()) return;

                // Register to xmpp and call events
                this.attachHandlers();
                await this.getUsersListsFromServer();

                // UsersList Service is ready to be used by other services/components
                this.serviceReady.next(true);

                this.logger.info(`[UsersListsService] === STARTED ===`);
                this.startInProgress = false;
            } else {
                this.logger.info(`[UsersListsService] === Start already in progress ! ===`);
            }
        }
        catch (error) {
            this.startInProgress = false;
            this.logger.error(`[UsersListsService] === STARTING FAILURE === ${error.message}`);
        }
    }

    public async stop(): Promise<void> {
        this.logger.info(`[UsersListsService] === STOPPING ===`);
        this.usersLists = [];
        this.serviceReady.next(false);

        // Remove handlers and subscriptions
        this.removeHandlers();
        this.logger.info(`[UsersListsService] === STOPPED ===`);
    }

    public reconnect(): void {
        this.logger.info('[UsersListsService] === RESTART ===');
        this.attachHandlers();
        this.logger.info('[UsersListsService] === RESTARTED ===');

    }

    private attachHandlers(): void {
        this.removeHandlers();
        this.xmppManagementHandler = this.xmppService.addHandler((stanza: any) => { this.onUsersListMessageReceived(stanza); return true; }, null, "message", "management");
    }

    private removeHandlers() {
        this.logger.debug(`[UsersListsService] removeHandlers `);
        // Remove all handlers
        if (this.xmppManagementHandler) {
            this.xmppService.deleteHandler(this.xmppManagementHandler);
            this.xmppManagementHandler = null;
        }
    }

    /**
     * Subscribe to the users lists updates, all events are of RBEvent<UsersListsServiceEvents>
     * @param callback - The callback function that will be called when events are received
     * @param eventNames - (optional) array of event to listen
     * @returns Returns RxJS Subscription
     */
    public subscribe(callback: (event: RBEvent<UsersListsServiceEvents>) => any, eventNames?: UsersListsServiceEvents | UsersListsServiceEvents[]): Subscription {
        const observable = eventNames
            ? this.rxSubject.pipe(filter((event: RBEvent<UsersListsServiceEvents>) => {
                if (eventNames instanceof Array) return eventNames.indexOf(event.name) !== -1;
                return event.name === eventNames;
            }))
            : this.rxSubject;

        return observable.subscribe((event: RBEvent<UsersListsServiceEvents>) => {
            try { callback(event); }
            catch (error) { this.logger.error(`[UsersListsService] subscribe () -- error -- ${error.message}`); }
        });
    }

    /**
     * UsersList service events send to other services via sendEvent()
     * Can listen to it via UsersListsServiceRB.subscribe() API
     * @param name - the name of the event
     * @param data - the data to be send (an object)
     */
    private sendEvent(name: UsersListsServiceEvents, data: any = null) {
        this.rxSubject.next(RBEvent.create(name, data));
    }

    // ------------------------------------------------------------
    //          EVENT LISTENERS ( Handling of events)
    // ------------------------------------------------------------

    /**
     * Management of XMPP management message
     * @param stanza - The received stanza.
     */
    private async onUsersListMessageReceived(stanza: any): Promise<void> {
        try {
            const stanzaData: any = {};
            stanzaData.outgoingMessage = false;
            stanzaData.body = $(stanza)[0].firstChild;
            stanzaData.messageId = $(stanza).attr("id");

            if (stanzaData.body) {
                if (stanzaData.body.tagName === "group") {
                    const action = stanzaData.body.getAttribute("action");
                    const scope = stanzaData.body.getAttribute("scope");
                    const listId = stanzaData.body.getAttribute("id");

                    switch (scope) {
                        // Management of messages relating to a users list
                        case "group": {
                            switch (action) {
                                case "create": {
                                    // Get infos from server for the new created users list and store it in cache
                                    const newList: UsersListRB = await this.addListToCache(listId);
                                    // Notification about the creation of a users list (The full list of data is specified in event)
                                    this.sendEvent(UsersListsServiceEvents.ON_USERS_LIST_CREATED, newList);
                                    break;
                                }

                                case "update": {
                                    const name = stanzaData.body.getAttribute("name");
                                    const comment = stanzaData.body.getAttribute("comment");
                                    const isFavorite = (stanzaData.body.getAttribute("isFavorite") === "true");
                                    const updatedList: UsersListRB = await this.updateListFomCache(listId, name, comment, isFavorite);
                                    // Notification about the update of the information of a users list (The list id is specified in event)
                                    this.sendEvent(UsersListsServiceEvents.ON_USERS_LIST_PROFILE_UPDATED, updatedList);
                                } break;

                                case "delete":
                                    this.removeListFomCache(listId);
                                    // Notification about the deletion of a users list (The list id is specified in event)
                                    this.sendEvent(UsersListsServiceEvents.ON_USERS_LIST_DELETED, listId);
                                    break;

                                default:
                                    break;
                            }
                            break;
                        }

                        // Management of messages relating to the members of a users list
                        case "user": {
                            const userId: string = stanzaData.body.getAttribute("userId");
                            const list: UsersList = this.getUsersListById(listId);

                            switch (action) {
                                case "create": {
                                    await this.addUserToCache(userId, listId);
                                    // Notification about the update of the users list (The list id and the id of the added user are specified in event)
                                    const addedUser: Contact = await this.contactService.getContact(userId);
                                    this.sendEvent(UsersListsServiceEvents.ON_USER_ADDED_TO_LIST, { user: addedUser, list: list });
                                    break;
                                }

                                case "delete":
                                    this.removeUserFromCache(userId, listId);
                                    // Notification about the update of the users list (The list id and the id of the added user are specified in event)
                                    this.sendEvent(UsersListsServiceEvents.ON_USER_REMOVED_FROM_LIST, { userId: userId, list: list });
                                    break;
                                default:
                                    break;
                            }
                            break;
                        }

                        default:
                            break;
                    }
                }
            }
        }
        catch (error) { /**/ }
    }

    // ------------------------------------------------------------
    //                      PRIVATE METHODS
    // ------------------------------------------------------------

    /**
     * Get all users lists from the server
     */
    private async getUsersListsFromServer(): Promise<UsersListRB[]> {
        try {
            this.logger.info(`[UsersListsService] getUsersListsFromServer`);

            const headers = this.authService.getRequestHeader();
            const responseData: Response = await fetch(`${this.portalURL}groups?format=full`, { method: 'GET', headers });
            if (!responseData.ok) { throw (responseData); }
            const response: any = await responseData.json();

            // Handle success response
            const data = response.data;
            this.logger.info(`[UsersListsService] getUsersListsFromServer success (found ${data.length} users list(s))`);

            this.usersLists = [];
            const usersPromises = [];

            // Create a UsersListRB instance for each retrieved users list
            data.forEach((listData: any) => {
                const list: UsersListRB = UsersListRB.createFromData(listData);

                // Add users list in cache
                this.usersLists.push(list);

                // Add users to list, as contact model
                listData.users.forEach((userId: string) => {
                    usersPromises.push(
                        this.contactService.getContactByDBId(userId).then((contact: Contact) => {
                            if (contact) { list.users.push(contact); }
                        })
                    );
                });
            });
            await allSettled(usersPromises);
            this.logUsersLists(this.usersLists);

            return this.usersLists;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getUsersListsFromServer');
            this.logger.error(`[UsersListsService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Add a users list in cache ('usersLists' data)
     * @param listId - The id of the list to add in cache
     * @returns The data associated with the new users list
     */
    public async addListToCache(listId: string): Promise<UsersListRB> {
        try {
            this.logger.info(`[UsersListsService] addListToCache for ${listId}`);

            // Get main information about the new users list
            const headers = this.authService.getRequestHeader();
            const responseData: Response = await fetch(`${this.portalURL}groups/${listId}`, { method: 'GET', headers });
            if (!responseData.ok) { throw (responseData); }
            const response: any = await responseData.json();

            // Handle success response
            const listData = response.data;

            // Get retrieves the list of users who are members of the new users list
            const usersPromises = [];
            const newList = UsersListRB.createFromData(listData);

            const result = this.usersLists.some((list: UsersListRB, index: number) => {
                // Case of list already present in cache (Is this use case relevant / useful ?)
                if (list.id === listData.id) {
                    this.usersLists[index] = newList;

                    // Create a promise for each user to add in the list
                    listData.users.forEach((user: any) => {
                        usersPromises.push(this.contactService.getContactByDBId(user.id).then((contact: Contact) => {
                            if (contact) {
                                this.usersLists[index].users.push(contact);
                            }
                        }));
                    });

                    // List found in cache and updated
                    return true;
                }

                // List not found in cache, will be added hereafter
                return false;
            });

            if (!result) {
                this.usersLists.push(newList);

                // Sort again the list of users list by list name
                this.usersLists.sort((list1: UsersListRB, list2: UsersListRB) => { return list1.name.localeCompare(list2.name); });

                // Create a promise for each user to add in the list
                listData.users.forEach((user: any) => {
                    usersPromises.push(this.contactService.getContactByDBId(user.id).then(function (contact) {
                        if (contact) {
                            newList.users.push(contact);
                        }
                    }));
                });
            }

            // Laucnh all users promises to retrieve a Contact for each of them and store it in users list cache
            await allSettled(usersPromises);

            this.logger.info(`[UsersListsService] addListToCache success for list name: ${newList.name} - id: ${newList.id}`);
            return newList;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'addListToCache');
            this.logger.error(`[UsersListsService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Update a users list from cache ('usersLists' data)
     * The modification may come from the client who performed the action, or from another connected client (from the same connected user).
     * @param listId - The id of the list to remove from cache
     * @param name - Name of the users list
     * @param description - A description of the users list.
     * @param isFavorite - Flag indicating if the users list is marked as "favorite".
     * @returns The data associated with the updated users list
     */
    private async updateListFomCache(listId: string, name: string, description: string, isFavorite: boolean): Promise<UsersListRB> {
        try {
            const updatedList = this.usersLists.find((list: UsersListRB) => { return list.id === listId; });

            if (!updatedList) {
                throw new Error(`[UsersListsService] updateListFomCache -- UsersList ${listId} not found in cache`);
            }

            updatedList.name = name;
            updatedList.description = description;
            updatedList.isFavorite = isFavorite;

            return updatedList;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updateListFomCache');
            this.logger.error(`[UsersListsService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Remove a users list from cache ('usersLists' data)
     * @param listId - The id of the list to remove from cache
     */
    private async removeListFomCache(listId: string): Promise<any> {
        try {
            const index = this.usersLists.findIndex((list) => { return list.id === listId; });
            if (index !== -1) { this.usersLists.splice(index, 1); }
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'removeListFomCache');
            this.logger.error(`[UsersListsServiceRB] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Add a user to the members of a users list in cache ('usersLists' data)
     * @param userId - The dbId of the user to add to users list
     * @param listId - The id of the users list to which the user must be added
     */
    private async addUserToCache(userId: string, listId: string): Promise<any> {
        try {
            const updatedList = this.usersLists.find((list: UsersListRB) => { return list.id === listId; });

            if (!updatedList) {
                throw new Error(`[UsersListsService] addUserToCache -- UsersList ${listId} not found in cache`);
            }

            // If user already in list, do nothing and return the list without any update
            let user: User = updatedList.users.find((usr: Contact) => { return userId === usr.dbId; });
            if (user) { return; }

            // Find contact associated to user  and add it to as memeber of the list
            user = await this.contactService.getContactByDBId(userId);
            if (!user) {
                throw new Error(`[UsersListsService] addUserToCache -- No contact found for user ${userId}`);
            }
            updatedList.users.push(user);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'addUserToCache');
            this.logger.error(`[UsersListsService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Remove a user from the members of a users list in cache  ('usersLists' data)
     * @param userId - The dbId of the user to remove from the list
     * @param listId - The id of the users list in which the user must be removed
     */
    private removeUserFromCache(userId: string, listId: string): void {
        this.usersLists.forEach((list: UsersListRB) => {
            if (list.id === listId) {
                list.users.forEach((userItem: Contact, index: number) => {
                    if (userItem.dbId === userId) {
                        list.users.splice(index, 1);
                    }
                });
            }
        });
    }

    /**
     * Log main infos for all retrieved users lists: list + members names
     * @param lists - The users lists internal cache
     */
    private logUsersLists(lists: UsersListRB[]): void {
        // If no users list defined, nothing to log
        if (!lists || lists.length === 0) { return; }

        lists.forEach((list) => {
            let listName = "";
            let UsersNameContent = "";
            if (list.name) {
                listName = list.name;
            }
            // Get log name of all users member of the list
            if (list.users && list.users.length !== 0) {
                UsersNameContent = list.users.map(function (user) {
                    const contact = user as Contact;
                    return contact.nameForLogs;
                }).join(" / ");
            }
            this.logger.info(`[UsersListsService] logUsersLists - List '${listName}' with ${list.users.length} users:  ${UsersNameContent}`);
        });
    }

    // ------------------------------------------------------------
    //                      PUBLIC METHODS
    //     Used by internal Services/Components/... or by SDK
    // ------------------------------------------------------------

    // USERS LIST MANAGEMENT (Add / Update / Delete)
    // -----------------------------------------------

    /**
     * Create a new list of users
     * Only the name of the users list is mandatory.
     * The other information (description and favorite status) are optional and can be set afterwards using the 'updateUsersListInfo' method.
     * A ON_USERS_LIST_CREATED event is sent afterwards at the completion of the operation to notify about the users list creation.
     * @param profile - Profile (general information) of the users list
     */
    public async createUsersList(profile: UsersListProfile): Promise<any> {
        try {
            let listDescription: string = "";
            let listIsFavorite: boolean = false;

            this.logger.info(`[UsersListsService] createUsersList: ${profile.name}`);

            if (!profile.name) {
                throw new Error("Error: A name must be specified to create the users list.");
            }

            if (profile.description) { listDescription = profile.description; }
            if (profile.isFavorite) { listIsFavorite = profile.isFavorite; }

            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({ name: profile.name, comment: listDescription, isFavorite: listIsFavorite });
            const responseData: Response = await fetch(`${this.portalURL}groups`, { method: 'POST', headers, body });
            if (!responseData.ok) { throw responseData; }

            // Handle success response
            this.logger.info(`[UsersListsService] createUsersList success for ${name}`);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'createUsersList');
            this.logger.error(`[UsersListsService] ${errorInfo.message}`);
            errorInfo.error.details = (error.status === 409) ? "conflict" : "unknown";
            throw errorInfo.error;
        }
    }

    /**
     * Delete a users list specified either by its reference or by its unique identifier.
     * A ON_USERS_LIST_DELETED event is sent afterwards at the completion of the operation to notify about the users list deletion.
     * @param listRef  - Reference or identifier of the users list to delete
     */
    public async deleteUsersList(listRef: string | UsersList): Promise<any> {
        try {
            let listId: string = "";

            if (listRef instanceof UsersListRB) {
                listId = (listRef).id;
            } else {
                listId = listRef as string;
            }

            if (!listId) {
                throw new Error("Error: No users list Id found");
            }

            this.logger.info(`[UsersListsService] deleteUsersList : ${listId}`);
            const headers = this.authService.getRequestHeader();
            const responseData: Response = await fetch(`${this.portalURL}groups/${listId}`, { method: 'DELETE', headers });
            if (!responseData.ok) { throw responseData; }
            this.removeListFomCache(listId);
            this.logger.info(`[UsersListsService] deleteUsersList success for ${listId}`);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'deleteUsersList');
            this.logger.error(`[UsersListsService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Update the general information associated with the users list
     * Only the general information, i.e. the name, the description and the favorite status can be updated using this method.
     * Users are added to or removed from the list using methods 'addUserToList' and 'removeUserFromList' respectively.
     * A ON_USERS_LIST_PROFILE_UPDATED event is sent afterwards at the completion of the operation to notify about the users list update.
     * @param list - The users list with the general information to be updated
     */
    public async updateUsersListInfo(list: UsersList): Promise<any> {
        try {
            this.logger.info(`[UsersListsService] updateUsersListInfo for ${list.name}`);

            if (!list.id) {
                throw new Error("Error: no users list Id found");
            }

            // The name list must not be empty even when updating information
            if (!list.name) {
                throw new Error("Error: The users list name must not be empty.");
            }

            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({ name: list.name, comment: list.description, isFavorite: list.isFavorite });
            const responseData: Response = await fetch(`${this.portalURL}groups/${list.id}`, { method: 'PUT', headers, body });
            if (!responseData.ok) { throw responseData; }
            this.logger.info(`[UsersListsService] updateUsersListInfo success for ${list.name}`);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updateUsersListInfo');
            this.logger.error(`[UsersListsService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    // USERS LIST MEMBERS MANAGEMENT (Add / Remove users in list)
    // --------------------------------------------------------------

    /**
     * Add a user in a users list.
     * A ON_USER_ADDED_TO_LIST event is sent afterwards at the completion of the operation to notify about adding the user to the list.
     * @param user - The user to add to users list
     * @param list - The users list to which the user is to be added
     */
    public async addUserToList(user: Contact, list: UsersList): Promise<void> {
        try {
            this.logger.info(`[UsersListsService] addUserToList`);

            // Checks that users list id is specified
            if (!list.id) {
                throw new Error(`Error: no users list Id found`);
            }

            // Checks that users list id is specified
            if (!user.dbId) {
                throw new Error(`Error: no user dbId found`);
            }

            // Check if the user is not already present in the list. If the case, an error is returned.
            const found = list.users.some((usr: Contact) => { return user.dbId === usr.dbId; });
            if (found) {
                const foundError: string = `[UsersListsService] addUserToList - user ${user.dbId} already exist in list ${list.name}`;
                throw new Error(foundError);
            }

            const headers = this.authService.getPostHeader();
            const responseData: Response = await fetch(`${this.portalURL}groups/${list.id}/users/${user.dbId}`, { method: 'POST', headers });
            if (!responseData.ok) { throw (responseData); }

            await this.addUserToCache(user.dbId, list.id);

            this.logger.info(`[UsersListsService] addUserToList success: ${user.dbId} to ${list.name}`);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'addUserToList');
            this.logger.error(`[UsersListsService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Remove a user from a users list.
     * A ON_USER_REMOVED_FROM_LIST event is sent afterwards at the completion of the operation to notify about removing a user from the list.
     * @param user - Reference of the user to remove from the list
     * @param list - The users list from which the user is to be removed
     */
    public async removeUserFromList(user: Contact, list: UsersList): Promise<any> {
        try {
            this.logger.info(`[UsersListsService] removeUserFromList - ${user.dbId} from ${list.name}`);

            // Check that users list id is specified
            if (!list.id) {
                throw new Error(`Error: no users list Id found`);
            }

            // Check that users list id is specified
            if (!user.dbId) {
                throw new Error(`Error: no user dbId found`);
            }

            const headers = this.authService.getRequestHeader();
            const responseData: Response = await fetch(`${this.portalURL}groups/${list.id}/users/${user.dbId}`, { method: 'DELETE', headers });
            if (!responseData.ok) { throw responseData; }

            this.removeUserFromCache(user.dbId, list.id);
            this.logger.info(`[UsersListsService] removeUserFromList success: ${user.dbId} from ${list.name}`);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'removeUserFromList');
            this.logger.error(`[UsersListsService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    // GET USERS LIST(s) DATA
    // --------------------------------------------------------------

    /**
     * Get all users lists data with their data
     */
    public getUsersLists(): UsersList[] {
        return this.usersLists;
    }

    /**
     * @public
     * Get the data associated with a specifid users list.
     * @param id - The unique identifier of the users list
     * @returns The data associated with a users list
     */
    public getUsersListById(id: string): UsersList {
        return this.usersLists.find((list: UsersList) => {
            return list.id === id;
        });
    }

    /**
     * @public
     * Get the data associated with a specifid users list.
     * @param criteria - The criteria to find from usersList.name
     * @returns The data associated with the found users list
     */
    public searchUsersListByName(criteria: string): UsersListRB[] { //RQRAINB-11637
        const queries = criteria.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').split(/[ -]+/);
        // return this.getUsersLists().filter((usersList: UsersListRB) => {
        const result = this.getUsersLists().filter((usersList: UsersListRB) => {
            usersList.filterName = usersList.name.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
            const names = usersList.filterName.split(/[ -]+/);
            return queries.every((query) => {
                return names.some((name, index) => {
                    if (name.length && name.indexOf(query) !== -1) { names[index] = ''; return true; }
                    return false;
                });
            });
        });
        return result;
    }
}
